/**
 * Once-per-session license gate — a tiny external store.
 *
 * ProtectedRoute resolves whether the signed-in user is a provider and whether they have license
 * data, then stores the result here at module scope. Each route wraps its own <ProtectedRoute>, so
 * the component remounts on every navigation and its state resets; persisting the result here means
 * the license check runs once per user and navigation between menus/pages is never re-gated or
 * re-fetched. Keyed by user id so a different signed-in user is always re-evaluated.
 *
 * Exposed as a subscribe/getSnapshot store so components can read it via useSyncExternalStore,
 * which is the tearing-safe way to read mutable external state during render (and re-renders
 * subscribers when the gate is set or invalidated).
 *
 * Lives in its own module (not in ProtectedRoute) to avoid a circular import between ProtectedRoute
 * and AuthContext, which already depend on each other.
 */
export interface LicenseGate {
  userId: string;
  isProvider: boolean;
  hasLicenseData: boolean;
}

let cachedLicenseGate: LicenseGate | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to gate changes. Returns an unsubscribe function. For useSyncExternalStore. */
export function subscribeLicenseGate(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Current gate snapshot (stable reference until set/invalidated, as useSyncExternalStore requires).
 * Returns the raw cached value regardless of user; callers should match on userId.
 */
export function getLicenseGateSnapshot(): LicenseGate | null {
  return cachedLicenseGate;
}

/** Returns the cached gate for the given user id, or null if not resolved for that user. */
export function getLicenseGate(userId: string): LicenseGate | null {
  return cachedLicenseGate?.userId === userId ? cachedLicenseGate : null;
}

/** Store the resolved gate for a user. */
export function setLicenseGate(gate: LicenseGate): void {
  cachedLicenseGate = gate;
  emit();
}

/**
 * Clear the cached gate so the next ProtectedRoute mount re-runs the check.
 * Call after a provider saves/changes license data, and on logout.
 */
export function invalidateLicenseGate(): void {
  if (cachedLicenseGate === null) return;
  cachedLicenseGate = null;
  emit();
}
